import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { getTradeLock } from '../components/helpers';
import { apiFetch } from '../api';
import {
  Page, Hero, StatStrip, ControlRail, Sheet, OptionList, Empty, Tag,
  useReveal, MoreButton,
  money, compactMoney, dateShort,
} from './mobileShared';

// The Locker — your synced Steam inventory. Tapping a card selects it; the
// selection bar then rises above the tab dock with the three things you can do
// with a batch. Extension pairing stays a desktop job, so this tab only reads.

const EXTERIOR_ABBR = {
  'Factory New': 'FN', 'Minimal Wear': 'MW', 'Field-Tested': 'FT',
  'Well-Worn': 'WW', 'Battle-Scarred': 'BS',
};
const EXTERIOR_RE = /\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/;

const FILTERS = [
  { key: 'all',       label: 'Everything' },
  { key: 'locked',    label: 'Trade locked' },
  { key: 'tradable',  label: 'Tradable' },
  { key: 'tracked',   label: 'Already in tracker' },
  { key: 'untracked', label: 'Not tracked yet' },
];

// Not a filter over the inventory but a separate list: items in a storage unit
// are absent from Steam's reading entirely, so "Everything" cannot include them
// without meaning something else. See the twins in SkinTable/InventoryTabZone.
const STORAGE_FILTER = { key: 'stored', label: 'In storage' };

const SORTS = [
  { key: 'price',    label: 'Highest price' },
  { key: 'name',     label: 'Name A–Z' },
  { key: 'float',    label: 'Lowest float' },
  { key: 'stickers', label: 'Most stickers' },
  { key: 'unlock',   label: 'Unlocking soonest' },
];

export default function MobileInventory({
  user, livePrices, syncedInventory, setSyncedInventory, storedItems = [],
  selectedForGroup, setSelectedForGroup,
  groupedPurchases, setGroupedPurchases,
  onOpenGroupModal, trackedAssetIds, onSendToTracker,
}) {
  const [loading, setLoading] = useState(false);
  const [syncMeta, setSyncMeta] = useState(null);
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort]     = useState('price');
  const [filters, setFilters] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res  = await apiFetch(`/api/tracker/inventory/${user}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.items?.length) setSyncedInventory(data.items);
        setSyncMeta(data.syncMeta || null);
        const gRes  = await apiFetch(`/api/grouped-purchases/${user}`);
        const gData = await gRes.json();
        if (!cancelled && gData.groupedPurchases) setGroupedPurchases(gData.groupedPurchases);
      } catch { /* non-fatal — the tab still renders whatever is already loaded */ }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stored items are read-only: Steam reissues the assetId when one comes back
  // out of the unit, so grouping or tracking it would file a dead id.
  const storedAssetIds = useMemo(
    () => new Set((storedItems || []).map(i => i.assetId).filter(Boolean)),
    [storedItems]
  );

  const toggleItem = assetId => {
    if (trackedAssetIds?.has(assetId)) return;
    if (storedAssetIds.has(assetId)) return;
    setSelectedForGroup(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
      return next;
    });
  };

  const items = useMemo(() => (syncedInventory || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    const { locked, until } = getTradeLock(it, null);
    return {
      it, mhn,
      name:     mhn.replace(EXTERIOR_RE, '').trim() || mhn,
      wear:     EXTERIOR_ABBR[it.exterior] || null,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked, until,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
    };
  }), [syncedInventory, livePrices, trackedAssetIds]);

  // The storage shelf, same shape so one card and one sort serve both. Trade
  // lock is blanked rather than read: the last reading of it predates the item
  // being put away and says nothing about now.
  const stored = useMemo(() => (storedItems || []).map(it => {
    const mhn = it.marketHashName || it.market_hash_name || '';
    return {
      it, mhn,
      name:     mhn.replace(EXTERIOR_RE, '').trim() || mhn,
      wear:     EXTERIOR_ABBR[it.exterior] || null,
      price:    livePrices?.[mhn]?.starting_at?.price ?? null,
      locked: false, until: null,
      stickers: (it.stickers || []).filter(s => s.name).length,
      tracked:  trackedAssetIds?.has(it.assetId) ?? false,
      stored:   true,
    };
  }), [storedItems, livePrices, trackedAssetIds]);

  const filterOptions = useMemo(
    () => (stored.length ? [...FILTERS, STORAGE_FILTER] : FILTERS),
    [stored.length]
  );

  // The last stored item coming back takes its option with it. Without this the
  // sheet would sit on a filter it no longer offers a way out of.
  useEffect(() => {
    if (filter === 'stored' && stored.length === 0) setFilter('all');
  }, [filter, stored.length]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Two separate pools, never mixed: nothing in storage is visible in Steam,
    // so no filter over the live inventory can honestly include it.
    const pool = filter === 'stored' ? stored : items;
    const list = pool.filter(e => {
      if (q && !e.mhn.toLowerCase().includes(q)) return false;
      if (filter === 'locked'    && !e.locked) return false;
      if (filter === 'tradable'  && e.locked) return false;
      if (filter === 'tracked'   && !e.tracked) return false;
      if (filter === 'untracked' && e.tracked) return false;
      return true;
    });
    const by = {
      price:    (a, b) => (b.price ?? -1) - (a.price ?? -1),
      name:     (a, b) => a.mhn.localeCompare(b.mhn),
      float:    (a, b) => (a.it.float ?? Infinity) - (b.it.float ?? Infinity),
      stickers: (a, b) => b.stickers - a.stickers,
      unlock:   (a, b) => (a.until ? +a.until : Infinity) - (b.until ? +b.until : Infinity),
    }[sort];
    return list.sort(by);
  }, [items, stored, query, filter, sort]);

  // Live items only. The headline numbers answer "what is in my Steam
  // inventory", and something sitting in a casket is not part of that answer.
  const totals = useMemo(() => ({
    value:       items.reduce((s, e) => s + (e.price ?? 0), 0),
    locked:      items.filter(e => e.locked).length,
    lockedValue: items.filter(e => e.locked).reduce((s, e) => s + (e.price ?? 0), 0),
    tracked:     items.filter(e => e.tracked).length,
    priced:      items.filter(e => e.price != null).length,
    count:       items.length,
  }), [items]);

  const [shown, hasMore, showMore] = useReveal(filtered, 16);
  const selCount = selectedForGroup?.size || 0;
  const activeFilters = (filter !== 'all' ? 1 : 0) + (sort !== 'price' ? 1 : 0);

  if (!loading && !items.length && !stored.length) {
    return (
      <Page>
        <Empty>
          No synced inventory yet. Pair the browser extension on desktop and run a sync;
          the items will show up here afterwards.
        </Empty>
      </Page>
    );
  }

  return (
    <Page className="has-selbar">
      <Hero
        label="Inventory market value"
        value={compactMoney(totals.value)}
        sub={`${totals.priced} of ${totals.count} item${totals.count !== 1 ? 's' : ''} have a live price`}
        foot={syncMeta?.syncCompletedAt
          ? `last synced ${dateShort(syncMeta.syncCompletedAt)}`
          : 'sync date unknown'}
      />

      <StatStrip items={[
        { label: 'Items', value: String(totals.count), sub: `${totals.tracked} already tracked` },
        { label: 'Trade locked', value: String(totals.locked), sub: `${money(totals.lockedValue)} frozen` },
        { label: 'Tradable', value: String(totals.count - totals.locked), sub: 'free to sell or trade' },
      ]} />

      <ControlRail
        query={query} onQuery={setQuery}
        placeholder="Search the locker…"
        activeCount={activeFilters}
        onOpen={() => setFilters(true)}
      />

      {loading && <div className="mb-loading">Loading inventory…</div>}

      {!loading && filtered.length === 0 ? (
        <Empty>Nothing in the locker matches your filters.</Empty>
      ) : (
        <>
          {filter === 'stored' && (
            <div className="mb-loading">
              Items you said are in a storage unit. Steam does not list what is inside one, so these
              are left out of every other view and total. They come back on their own once a sync
              sees them.
            </div>
          )}
          <div className="mb-grid2">
            {shown.map((e, i) => {
              const selected = selectedForGroup?.has(e.it.assetId);
              return (
                <motion.button
                  key={e.it.assetId || i}
                  className={`mb-card tap ${selected ? 'is-selected' : ''} ${e.tracked || e.stored ? 'is-tracked' : ''}`}
                  onClick={() => toggleItem(e.it.assetId)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 10) * 0.02 }}
                >
                  <div className="mb-card-stage">
                    {e.it.iconUrl || e.it.image
                      ? <img src={e.it.iconUrl || e.it.image} alt="" loading="lazy" />
                      : <span className="mb-thumb-blank" />}
                    {e.locked && (
                      <span className="mb-lock">
                        {e.until ? `🔒 ${dateShort(e.until)}` : '🔒 Locked'}
                      </span>
                    )}
                    {selected && <span className="mb-tick" aria-hidden="true">✓</span>}
                  </div>
                  <div className="mb-card-name">{e.name}</div>
                  <div className="mb-card-tags">
                    {e.wear && <Tag>{e.wear}</Tag>}
                    {e.stickers > 0 && <Tag>{e.stickers} sticker{e.stickers !== 1 ? 's' : ''}</Tag>}
                    {e.stored && <Tag>In storage</Tag>}
                    {e.tracked && <Tag tone="good">Tracked</Tag>}
                  </div>
                  <div className="mb-card-prices">
                    <span><i>Market</i>{e.price != null ? money(e.price) : 'n/a'}</span>
                    <span><i>Float</i>{e.it.float != null ? Number(e.it.float).toFixed(4) : 'n/a'}</span>
                  </div>
                </motion.button>
              );
            })}
          </div>
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}

      {selCount > 0 && (
        <motion.div
          className="mb-selbar"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        >
          <span className="mb-selbar-count">
            {selCount} selected
            <button onClick={() => setSelectedForGroup(new Set())}>clear</button>
          </span>
          <div className="mb-selbar-actions">
            <button className="mb-btn" onClick={() => onSendToTracker?.(Array.from(selectedForGroup), false)}>
              Track
            </button>
            <button className="mb-btn" onClick={() => onSendToTracker?.(Array.from(selectedForGroup), true)}>
              Invest
            </button>
            <button className="mb-btn primary" onClick={onOpenGroupModal}>Group buy</button>
          </div>
        </motion.div>
      )}

      <Sheet open={filters} onClose={() => setFilters(false)} title="Filter & sort"
        footer={<button className="mb-btn ghost" onClick={() => { setFilter('all'); setSort('price'); }}>Reset</button>}>
        <OptionList label="Show" value={filter} onChange={v => { setFilter(v); setFilters(false); }} options={filterOptions} />
        <OptionList label="Sort by" value={sort} onChange={v => { setSort(v); setFilters(false); }} options={SORTS} />
      </Sheet>
    </Page>
  );
}
